/**
 * src/components/RoadblockRadar.jsx — Roadblock Radar
 * AC Nautitech Track B
 *
 * Aggregates blocked items: tasks blocked, late POs, unresolved questions.
 * Shown on Focus tab for CEO. Consumes shared context — no independent fetches.
 */

import { useMemo } from 'react';
import { AlertTriangle, AlertCircle } from 'lucide-react';
import { useAppDataContext } from '../../lib/AppDataContext';
import { resolvePersonName, daysUntilDue } from '../../lib/mockData';
import { GlassCard, StatusChip, DueBadge } from '../../components/SharedUI';
import { isDone, isBlocked } from '../../lib/constants/taskStatus';

export default function RoadblockRadar() {
  const { tasks, projects, people, loading } = useAppDataContext();

  const roadblocks = useMemo(() => {
    const blocked = tasks.filter(t => isBlocked(t.status));
    const overdue = tasks.filter(t => {
      const d = daysUntilDue(t.due_date);
      return d !== null && d < 0 && !isDone(t.status);
    });

    return { blocked, overdue };
  }, [tasks]);

  const totalIssues = roadblocks.blocked.length + roadblocks.overdue.length;

  if (loading) return null;
  if (totalIssues === 0) return null;

  return (
    <GlassCard style={{
      border: '1px solid rgba(229,62,62,0.15)',
      background: 'rgba(229,62,62,0.04)',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
        <AlertTriangle size={18} color="var(--bad)" strokeWidth={1.5} />
        <span style={{ fontSize: 15, fontWeight: 600, color: 'var(--bad)' }}>Roadblock Radar</span>
        <span style={{
          marginLeft: 'auto', background: 'var(--bad-tint)', color: 'var(--bad)',
          padding: '2px 8px', borderRadius: 9999, fontSize: 11, fontWeight: 600,
        }}>{totalIssues}</span>
      </div>

      {/* Blocked tasks */}
      {roadblocks.blocked.map(task => {
        const assignee = task.user_profiles?.display_name || resolvePersonName(task.assignee_id, people);
        const proj = projects.find(p => p.id === task.project_id);
        return (
          <div key={task.id} style={{
            display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0',
            borderBottom: '1px solid var(--bdr)',
          }}>
            <AlertCircle size={14} color="var(--bad)" strokeWidth={1.5} />
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--t1)' }}>{task.task_name}</div>
              <div style={{ fontSize: 11, color: 'var(--tm)' }}>
                {proj?.project_name} · {assignee}
              </div>
            </div>
            <StatusChip status="Blocked" />
          </div>
        );
      })}

      {/* Overdue tasks */}
      {roadblocks.overdue.filter(t => t.status !== 'blocked').map(task => {
        const assignee = task.user_profiles?.display_name || resolvePersonName(task.assignee_id, people);
        const proj = projects.find(p => p.id === task.project_id);
        return (
          <div key={task.id} style={{
            display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0',
            borderBottom: '1px solid var(--bdr)',
          }}>
            <AlertTriangle size={14} color="var(--warn)" strokeWidth={1.5} />
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--t1)' }}>{task.task_name}</div>
              <div style={{ fontSize: 11, color: 'var(--tm)' }}>
                {proj?.project_name} · {assignee}
              </div>
            </div>
            <DueBadge dueDate={task.due_date} />
          </div>
        );
      })}
    </GlassCard>
  );
}
